/**
 * GuestView — the page guests open when they click the share link.
 *
 * URL: /join?room=streampersona-abc123&token=<jwt>
 *
 * Flow:
 *   1. Parse room + token from URL params
 *   2. Fetch LiveKit URL from backend (/api/call/token?room=xxx)
 *   3. Connect to LiveKit room as guest
 *   4. Subscribe to host's video/audio (face-swapped + cloned voice)
 *   5. Publish guest's own camera + mic back to host
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteParticipant,
  createLocalVideoTrack,
  createLocalAudioTrack,
} from "livekit-client";
import { API_BASE } from "@/lib/api";

type GuestState = "loading" | "connecting" | "connected" | "error" | "ended";

export function GuestView() {
  const [state,       setState]       = useState<GuestState>("loading");
  const [error,       setError]       = useState<string | null>(null);
  const [hostJoined,  setHostJoined]  = useState(false);
  const [roomName,    setRoomName]    = useState<string>("");

  const roomRef       = useRef<Room | null>(null);
  const hostVideoRef  = useRef<HTMLVideoElement>(null!);
  const localVideoRef = useRef<HTMLVideoElement>(null!);

  const lg = (...a: unknown[]) => console.log("[GuestView]", ...a);
  const le = (...a: unknown[]) => console.error("[GuestView]", ...a);

  const connect = useCallback(async () => {
    setState("connecting");
    setError(null);

    // Parse URL params
    const params    = new URLSearchParams(window.location.search);
    const room      = params.get("room");
    const tokenParam = params.get("token");

    if (!room) {
      setError("Missing room parameter in URL");
      setState("error");
      return;
    }

    setRoomName(room);
    lg(`Joining room: ${room}`);

    try {
      // Get LiveKit URL from backend
      // Always use the token from the URL — it was pre-generated for this guest
      let token = tokenParam;
      let livekitUrl: string;

      const statusRes = await fetch(`${API_BASE()}/api/call/status`);
      if (statusRes.ok) {
        const status = await statusRes.json();
        livekitUrl   = status.livekit_url || "";
        lg(`Got LiveKit URL from status: ${livekitUrl}`);
      } else {
        throw new Error("Backend unreachable — is the StreamPersona backend running?");
      }

      // If no token in URL, fetch one from backend
      if (!token) {
        const tokenRes = await fetch(`${API_BASE()}/api/call/token?room=${encodeURIComponent(room)}`);
        if (!tokenRes.ok) throw new Error("Could not get guest token from backend");
        const td = await tokenRes.json();
        token = td.token;
        if (!livekitUrl) livekitUrl = td.livekit_url;
      }

      if (!token) throw new Error("No token available — link may be invalid");
      if (!livekitUrl) throw new Error("Could not determine LiveKit URL");

      // Create room
      const lkRoom = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = lkRoom;

      // Wire events
      lkRoom.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        lg(`Participant joined: ${p.identity}`);
        if (p.identity === "host") setHostJoined(true);
      });
      lkRoom.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        lg(`Participant left: ${p.identity}`);
        if (p.identity === "host") { setHostJoined(false); setState("ended"); }
      });
      lkRoom.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        lg(`Track subscribed: ${track.kind} from ${participant.identity}`);
        if (track.kind === Track.Kind.Video && hostVideoRef.current) {
          track.attach(hostVideoRef.current);
          setHostJoined(true);
        }
        if (track.kind === Track.Kind.Audio) {
          track.attach();
        }
      });
      lkRoom.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach();
      });
      lkRoom.on(RoomEvent.Disconnected, () => {
        setState("ended");
      });

      // Connect
      await lkRoom.connect(livekitUrl, token);
      lg("Connected to LiveKit room as guest");

      // Check for existing participants (host already connected)
      lkRoom.remoteParticipants.forEach((p) => {
        if (p.identity === "host") setHostJoined(true);
        p.trackPublications.forEach((pub) => {
          if (pub.track && pub.track.kind === Track.Kind.Video && hostVideoRef.current) {
            pub.track.attach(hostVideoRef.current);
          }
          if (pub.track && pub.track.kind === Track.Kind.Audio) {
            pub.track.attach();
          }
        });
      });

      // Publish guest camera + mic
      try {
        const videoTrack = await createLocalVideoTrack({ resolution: { width: 640, height: 480 } });
        await lkRoom.localParticipant.publishTrack(videoTrack);
        if (localVideoRef.current) videoTrack.attach(localVideoRef.current);
        lg("Guest video published");
      } catch (e) { le("Guest video failed:", e); }

      try {
        const audioTrack = await createLocalAudioTrack();
        await lkRoom.localParticipant.publishTrack(audioTrack);
        lg("Guest audio published");
      } catch (e) { le("Guest audio failed:", e); }

      setState("connected");

    } catch (err) {
      le("Connection failed:", err);
      setError(err instanceof Error ? err.message : String(err));
      setState("error");
    }
  }, []);

  const connectedRef = useRef(false);
  useEffect(() => {
    if (connectedRef.current) return; // prevent StrictMode double-invoke
    connectedRef.current = true;
    connect();
    return () => {
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, []); // empty deps — connect once on mount

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      fontFamily: "var(--font-sans)", background: "var(--bg-base)", color: "var(--text-primary)",
      minHeight: "100vh", padding: "var(--space-6)", maxWidth: 800, margin: "0 auto",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>StreamPersona Call</div>
        <div style={{
          fontSize: 12, padding: "3px 10px", borderRadius: 20, fontWeight: 600,
          background: state === "connected" ? "#14532d" : state === "connecting" ? "#1c1917" : "#450a0a",
          border: `1px solid ${state === "connected" ? "#16a34a" : state === "connecting" ? "#44403c" : "#dc2626"}`,
          color: state === "connected" ? "#4ade80" : state === "connecting" ? "#f59e0b" : "#f87171",
        }}>
          {state === "loading" ? "Loading..." :
           state === "connecting" ? "Connecting..." :
           state === "connected" ? (hostJoined ? "In Call" : "Waiting for host...") :
           state === "ended" ? "Call Ended" : "Error"}
        </div>
        {roomName && <div style={{ fontSize: 11, color: "#4b5563" }}>Room: {roomName}</div>}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#450a0a", border: "1px solid #dc2626", borderRadius: 8,
          padding: "12px 16px", marginBottom: 16, fontSize: 14, color: "#fca5a5",
        }}>
          <strong>Connection failed:</strong> {error}
          <br />
          <button onClick={connect} style={{
            marginTop: 10, background: "#2563eb", color: "#fff", border: "none",
            borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer",
          }}>
            Retry
          </button>
        </div>
      )}

      {/* Call ended */}
      {state === "ended" && (
        <div style={{
          background: "#1a1a1a", borderRadius: 8, padding: "20px",
          textAlign: "center", color: "#9ca3af", fontSize: 14,
        }}>
          The call has ended.
        </div>
      )}

      {/* Video panels */}
      {(state === "connected" || state === "connecting") && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {/* Host video (face-swapped) */}
          <div style={{ flex: 2, minWidth: 300 }}>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>
              Host {hostJoined ? <span style={{ color: "#4ade80" }}>● Live</span> : <span style={{ color: "#f59e0b" }}>— waiting...</span>}
            </div>
            <div style={{ position: "relative", background: "#111", borderRadius: 8, minHeight: 240 }}>
              <video
                ref={hostVideoRef}
                autoPlay
                playsInline
                style={{ width: "100%", borderRadius: 8, display: "block", minHeight: 240 }}
              />
              {!hostJoined && (
                <div style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  color: "#4b5563", fontSize: 13,
                }}>Waiting for host...</div>
              )}
            </div>
          </div>

          {/* Guest video (self) */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>You</div>
            <div style={{ background: "#111", borderRadius: 8, minHeight: 160 }}>
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", borderRadius: 8, display: "block", transform: "scaleX(-1)" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 16, fontSize: 11, color: "#374151" }}>
        Powered by StreamPersona · LiveKit WebRTC
      </div>
    </div>
  );
}
